import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { authAuditApi } from '../api/authAudit.api';
import { useAuth } from '@/contexts/AuthContext';
import useInLeadershipShell from '@/hooks/useInLeadershipShell';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import { canAccessRoute } from '@/utils/roleAccess';

function todayIST() {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoIST(n) {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AuthAuditPage() {
  const navigate = useNavigate();
  const lead = useInLeadershipShell();
  const { user } = useAuth();
  const [from, setFrom] = useState(() => daysAgoIST(30));
  const [to, setTo] = useState(() => todayIST());
  const [email, setEmail] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ success: 0, failure: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const allowed = canAccessRoute(user, {
    allowedRoles: ['admin', 'super_admin', 'management'],
    anyPermission: ['audit.auth.view'],
  });

  const load = async () => {
    if (!allowed) return;
    setLoading(true);
    const params = { from, to, limit: 200 };
    if (email.trim()) params.email = email.trim();
    if (outcome === 'success') params.success = true;
    if (outcome === 'failure') params.success = false;
    try {
      const [list, sum] = await Promise.all([
        authAuditApi.list(params),
        authAuditApi.summary({ from, to }),
      ]);
      setItems(list.items || []);
      setSummary(sum.summary || { success: 0, failure: 0, total: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load auth audit');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-zinc-600">
        Auth audit is available to Admin, Dean, FIC, and Director.
      </div>
    );
  }

  const refreshBtn = (
    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
      <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
    </Button>
  );

  const body = (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          ['Total', summary.total, 'bg-zinc-800'],
          ['Success', summary.success, 'bg-emerald-600'],
          ['Failed', summary.failure, 'bg-rose-600'],
        ].map(([label, value, cls]) => (
          <div key={label} className={`rounded-xl text-white p-4 ${cls}`}>
            <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
            <div className="text-2xl font-semibold mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl p-4 grid sm:grid-cols-4 gap-3 items-end">
        <div className="space-y-1">
          <Label>From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Email / ID</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Filter" />
        </div>
        <div className="space-y-1">
          <Label>Outcome</Label>
          <select className="w-full h-10 border rounded-md px-2 text-sm" value={outcome}
            onChange={(e) => setOutcome(e.target.value)}>
            <option value="all">All</option>
            <option value="success">Success only</option>
            <option value="failure">Failures only</option>
          </select>
        </div>
        <div className="sm:col-span-4">
          <Button onClick={load} disabled={loading} data-testid="auth-audit-apply">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Apply filters
          </Button>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="auth-audit-table">
          <thead className="bg-zinc-50 text-left text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2">Time (UTC)</th>
              <th className="px-3 py-2">Identifier</th>
              <th className="px-3 py-2">Result</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2">Role</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-500">No events in this range</td></tr>
            ) : items.map((row, i) => (
              <tr key={`${row.created_at}-${i}`} className="border-t">
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{row.created_at?.replace('T', ' ').slice(0, 19)}</td>
                <td className="px-3 py-2">{row.identifier}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${row.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {row.success ? 'Success' : 'Failed'}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-600">{row.reason}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.ip}</td>
                <td className="px-3 py-2 text-zinc-600">{row.auth_method}</td>
                <td className="px-3 py-2 text-zinc-600">{row.clinic_role || row.role || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (lead) {
    return (
      <div className={LEADERSHIP_CONTENT} data-testid="auth-audit-page">
        <LeadershipPageHeader
          title="Authentication audit"
          subtitle="Login attempts with IP and outcome"
          actions={refreshBtn}
        />
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8EEF5]" data-testid="auth-audit-page">
      <header className="border-b px-4 py-4 sm:px-8 text-white" style={{ background: 'linear-gradient(120deg,#040E1A,#071B34,#0C2744)' }}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/overview')} className="text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2 font-heading">
                <Shield className="h-5 w-5 text-[#C4A35A]" /> Authentication audit
              </h1>
              <p className="text-xs text-[#9AADBE]">IIT Hyderabad Hospital · login attempts with IP</p>
            </div>
          </div>
          {refreshBtn}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 sm:px-8">{body}</main>
    </div>
  );
}
